import { OAuth2Type } from "@shared/contexts";
import { getDeviceInfo } from "@shared/utils";
import {
  OpenAPI,
  RedirectionResponse,
  SuccessResponse,
  SuccessTokenStepResponse,
} from "@shared/services";
import { request as __request } from "@shared/services/auth/request";
import { withAntiBotTokenRetry, resolveAntiBotToken } from "@shared/utils/recaptchaTokenHandler";
import i18n from "@i18n/index";

const TIMEOUT_MESSAGE_INDICATORS = ['timeout', 'Timeout', 'ECONNABORTED'];
const TIMEOUT_CODES = ['ECONNABORTED'];

const NETWORK_MESSAGE_INDICATORS = ['Network Error', 'ERR_NETWORK', 'ERR_INTERNET_DISCONNECTED'];
const NETWORK_CODES = ['ERR_NETWORK', 'ERR_INTERNET_DISCONNECTED'];

const CANCEL_MESSAGE_INDICATORS = ['Request aborted', 'AbortError'];
const CANCEL_NAMES = ['AbortError'];

function messageMatchesAny(error: any, indicators: string[]): boolean {
  return indicators.some(ind => error?.message?.includes(ind));
}

export function isTimeoutError(error: any): boolean {
  return (
    messageMatchesAny(error, TIMEOUT_MESSAGE_INDICATORS) ||
    TIMEOUT_CODES.includes(error?.code)
  );
}

export function isNetworkError(error: any): boolean {
  return (
    messageMatchesAny(error, NETWORK_MESSAGE_INDICATORS) ||
    NETWORK_CODES.includes(error?.code)
  );
}

function isCancellationError(error: any): boolean {
  return (
    messageMatchesAny(error, CANCEL_MESSAGE_INDICATORS) ||
    CANCEL_NAMES.includes(error?.name)
  );
}

const getErrorRequestCancelled = () => i18n.t('error.runtime.requestCancelled');
const getErrorRequestTimeout = () => i18n.t('error.runtime.requestTimeout');
const getErrorNetwork = () => i18n.t('error.runtime.networkError');
const IS_TEST_ENV = process.env.NODE_ENV === 'test';
const LOG_REQUEST_TIMEOUT = 'Request timeout:';

type ErrorClassifier = {
  check: (e: any) => boolean;
  getMessage: () => string;
  logMsg?: string;
};

const ERROR_CLASSIFIERS: ErrorClassifier[] = [
  { check: isTimeoutError, getMessage: getErrorRequestTimeout, logMsg: LOG_REQUEST_TIMEOUT },
  { check: isNetworkError, getMessage: getErrorNetwork },
  { check: isCancellationError, getMessage: getErrorRequestCancelled },
];

function logClassifiedError(logMsg: string | undefined, message: string, error: any): void {
  if (IS_TEST_ENV) return;
  const label = logMsg || message;
  const detail = logMsg ? ((error as Error).message || error) : '';
  console.warn(label, detail);
}

function classifyAndThrow(error: any): never {
  for (const { check, getMessage, logMsg } of ERROR_CLASSIFIERS) {
    if (check(error)) {
      const message = getMessage();
      logClassifiedError(logMsg, message, error);
      throw new Error(message);
    }
  }
  throw error;
}

function handleRequestError(error: any): never {
  if (!IS_TEST_ENV) {
    console.error('StartAuthenticationRequest error:', error);
  }
  classifyAndThrow(error);
}

function requestWithAbort<T>(
  signal: AbortSignal,
  requestFn: () => Promise<T>
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const abortHandler = () => {
      reject(new Error(getErrorRequestCancelled()));
    };

    signal.addEventListener('abort', abortHandler);

    requestFn()
      .then((response) => {
        signal.removeEventListener('abort', abortHandler);
        resolve(response);
      })
      .catch((error) => {
        signal.removeEventListener('abort', abortHandler);
        reject(error);
      });
  });
}

function validateStartAuthPreconditions(signal: AbortSignal | undefined): void {
  if (signal?.aborted) {
    throw new Error(getErrorRequestCancelled());
  }
}

async function executeStartAuthRequest(
  oauth2Data: OAuth2Type,
  _antiBotToken?: string
): Promise<SuccessTokenStepResponse> {
  const startTransactionDatas = {
    device_info: await getDeviceInfo(),
    client: oauth2Data,
  };

  OpenAPI.TOKEN = undefined;

  return __request<SuccessTokenStepResponse>(OpenAPI, {
    method: "POST",
    url: "/auth/user/start_authentication",
    body: startTransactionDatas,
    mediaType: "application/json",
  });
}

export async function StartAuthenticationRequest(
  oauth2Data: OAuth2Type,
  signal: AbortSignal | undefined,
  antiBotToken?: string
): Promise<SuccessTokenStepResponse> {
  try {
    validateStartAuthPreconditions(signal);

    const doRequest = () => executeStartAuthRequest(oauth2Data);

    return signal
      ? await requestWithAbort(signal, doRequest)
      : await doRequest();
  } catch (error) {
    handleRequestError(error);
  }
}

export async function IdentityRequest(
  oauth2Data: OAuth2Type,
  username: string,
  token: string | undefined,
  antiBotToken?: string
): Promise<SuccessTokenStepResponse> {
  try {
    return await withAntiBotTokenRetry(
      async (oauth2: OAuth2Type, user: string, authToken: string | undefined, _botToken?: string) => {
        const identityAuthenticationRequestDatas = {
          identity: user,
          client: oauth2,
        };

        OpenAPI.TOKEN = authToken;

        const resolvedToken = await resolveAntiBotToken(undefined);
        const headers = { 'X-Anti-Bot': resolvedToken };

        return await __request<SuccessTokenStepResponse>(OpenAPI, {
          method: "POST",
          url: "/auth/user/identity",
          body: identityAuthenticationRequestDatas,
          mediaType: "application/json",
          headers,
        });
      },
      [oauth2Data, username, token, antiBotToken],
      antiBotToken,
      (params, newToken) => [params[0], params[1], params[2], newToken]
    );
  } catch (error) {
    console.error('IdentityRequest error:', error);
    classifyAndThrow(error);
  }
}

const IS_DEV_ENV = process.env.NODE_ENV === 'development';

function logPasswordRequestStart(oauth2Data: OAuth2Type, password: string, token: string | undefined): void {
  if (!IS_DEV_ENV) return;
  console.log('[PasswordRequest] Starting password authentication request:', {
    hasOAuth2Data: !!oauth2Data,
    clientId: oauth2Data?.client_id,
    redirectUri: oauth2Data?.redirect_uri,
    hasPassword: !!password,
    passwordLength: password?.length,
    hasToken: !!token,
  });
}

function logPasswordRequestData(passwordAuthenticationRequestDatas: any): void {
  if (!IS_DEV_ENV) return;
  console.log('[PasswordRequest] Token set, making API request...');
  console.log('[PasswordRequest] Request data:', {
    hasPassword: !!passwordAuthenticationRequestDatas.password,
    hasClient: !!passwordAuthenticationRequestDatas.client,
    clientId: passwordAuthenticationRequestDatas.client?.client_id,
  });
}

function logPasswordResponse(response: any): void {
  if (!IS_DEV_ENV) return;
  const keys = response ? Object.keys(response) : [];
  console.log('[PasswordRequest] API response received:', {
    responseType: typeof response,
    responseKeys: keys,
  });
}

function logPasswordError(error: any): void {
  console.error('[PasswordRequest] Error occurred:', error);
}

export async function PasswordRequest(
  oauth2Data: OAuth2Type,
  password: string,
  token: string | undefined,
  antiBotToken?: string
): Promise<SuccessTokenStepResponse | RedirectionResponse> {
  try {
    logPasswordRequestStart(oauth2Data, password, token);

    return await withAntiBotTokenRetry(
      async (oauth2: OAuth2Type, pwd: string, authToken: string | undefined, _botToken?: string) => {
        const passwordAuthenticationRequestDatas = {
          password: pwd,
          client: oauth2,
        };

        OpenAPI.TOKEN = authToken;
        logPasswordRequestData(passwordAuthenticationRequestDatas);

        const resolvedToken = await resolveAntiBotToken(undefined);
        const headers = { 'X-Anti-Bot': resolvedToken };

        const response = await __request<SuccessTokenStepResponse | RedirectionResponse>(OpenAPI, {
          method: "POST",
          url: "/auth/user/password",
          body: passwordAuthenticationRequestDatas,
          mediaType: "application/json",
          headers,
        });

        logPasswordResponse(response);
        return response;
      },
      [oauth2Data, password, token, antiBotToken],
      antiBotToken,
      (params, newToken) => [params[0], params[1], params[2], newToken]
    );
  } catch (error) {
    logPasswordError(error);
    return classifyAndThrow(error);
  }
}

export async function SendRecoveryPasswordLinkRequest(
  email: string,
  state: string,
  token: string | undefined,
  antiBotToken: string | undefined,
  oauth2Data: OAuth2Type
): Promise<SuccessTokenStepResponse> {
  return withAntiBotTokenRetry(
    async (emailAddr: string, stateValue: string, authToken: string | undefined, _botToken: string | undefined, oauth2: OAuth2Type) => {
      const recoveryPasswordLinkRequestDatas = {
        identity: emailAddr,
        state: stateValue,
        client: {
          client_id: oauth2.client_id,
          redirect_uri: oauth2.redirect_uri,
          response_type: oauth2.response_type,
          scope: oauth2.scope,
          ...(oauth2.code_challenge && { code_challenge: oauth2.code_challenge }),
          ...(oauth2.code_challenge_method && { code_challenge_method: oauth2.code_challenge_method }),
        },
      };

      OpenAPI.TOKEN = authToken;

      const resolvedToken = await resolveAntiBotToken(undefined);
      const headers = { 'X-Anti-Bot': resolvedToken };

      const response = await __request<SuccessTokenStepResponse>(OpenAPI, {
        method: "POST",
        url: "/auth/user/send_recovery_password_link",
        body: recoveryPasswordLinkRequestDatas,
        mediaType: "application/json",
        headers,
      });

      console.log("send link");

      return response;
    },
    [email, state, token, antiBotToken, oauth2Data],
    antiBotToken,
    (params, newToken) => [params[0], params[1], params[2], newToken, params[4]]
  );
}

export async function RecoveryPasswordRequest(
  password: string,
  passwordConfirmation: string,
  token: string | undefined,
  antiBotToken?: string
): Promise<SuccessTokenStepResponse> {
  return withAntiBotTokenRetry(
    async (pwd: string, pwdConfirmation: string, authToken: string | undefined, _botToken?: string) => {
      const recoveryPasswordDatas = {
        password: pwd,
        password_confirmation: pwdConfirmation,
      };

      OpenAPI.TOKEN = authToken;

      const resolvedToken = await resolveAntiBotToken(undefined);
      const headers = { 'X-Anti-Bot': resolvedToken };

      return await __request<SuccessTokenStepResponse>(OpenAPI, {
        method: "POST",
        url: "/auth/user/recovery_password",
        body: recoveryPasswordDatas,
        mediaType: "application/json",
        headers,
      });
    },
    [password, passwordConfirmation, token, antiBotToken],
    antiBotToken,
    (params, newToken) => [params[0], params[1], params[2], newToken]
  );
}

export async function SecondFactorRequest(
  accessToken: string,
  factorType: "authenticator_app",
  oauth2Data: OAuth2Type,
  token: string | undefined,
  antiBotToken?: string
): Promise<RedirectionResponse> {
  return withAntiBotTokenRetry(
    async (accToken: string, factor: "authenticator_app", oauth2: OAuth2Type, authToken: string | undefined, _botToken?: string) => {
      const secondFactorDatas = {
        access_token: accToken,
        factor_type: factor,
        client: oauth2,
      };

      OpenAPI.TOKEN = authToken;

      const resolvedToken = await resolveAntiBotToken(undefined);
      const headers = { 'X-Anti-Bot': resolvedToken };

      return await __request<RedirectionResponse>(OpenAPI, {
        method: "POST",
        url: "/auth/user/second_factor",
        body: secondFactorDatas,
        mediaType: "application/json",
        headers,
      });
    },
    [accessToken, factorType, oauth2Data, token, antiBotToken],
    antiBotToken,
    (params, newToken) => [params[0], params[1], params[2], params[3], newToken]
  );
}

export async function VerifyEmailPost(
  confirmationCode: string,
  oauth2Data: OAuth2Type,
  token: string | undefined,
  antiBotToken?: string
): Promise<SuccessResponse> {
  return withAntiBotTokenRetry(
    async (code: string, oauth2: OAuth2Type, authToken: string | undefined, _botToken?: string) => {
      const commitDatas = {
        confirmation_code: parseInt(code),
        client: oauth2,
      };

      OpenAPI.TOKEN = authToken;

      const resolvedToken = await resolveAntiBotToken(undefined);
      const headers = { 'X-Anti-Bot': resolvedToken };

      return await __request<SuccessResponse>(OpenAPI, {
        method: "POST",
        url: "/auth/user/verify_email",
        body: commitDatas,
        mediaType: "application/json",
        headers,
      });
    },
    [confirmationCode, oauth2Data, token, antiBotToken],
    antiBotToken,
    (params, newToken) => [params[0], params[1], params[2], newToken]
  );
}
